/**
 * DSNY schedule helpers.
 * - Proposal phase uses getDSNYFrequencies(): returns counts per pickup type.
 * - Contract phase uses getDSNYPickupDays(): returns service days (evening before pickup).
 *
 * DSNY API/proxy return pickup days. RBC service happens the evening before pickup,
 * so UI schedules use service days while dsny_pickup_days stores raw pickup day names.
 */

export interface DSNYSchedules {
  garbage: number[]
  recycling: number[]
  organics: number[]
  bulk: number[]
}

export interface DSNYResult {
  address: string
  zone: string
  /** Service days (0=Sun … 6=Sat) — evening before DSNY pickup */
  schedules: DSNYSchedules
  /** Raw DSNY pickup days — for contract.dsny_pickup_days / job generation */
  pickupSchedules: DSNYSchedules
  nextPickup?: string
  source?: string
}

export interface DSNYFrequencies {
  garbage: number
  recycling: number
  organics: number
  bulk: number
  total: number
}

const dayToNumber: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
}

export const DAY_NUMBER_TO_NAME = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const

/** Official DSNY geocoder first; donateNYC shell is kept as fallback. */
export const DSNY_COLLECTION_API_URLS = [
  'https://dsnypublic.nyc.gov/dsny/api/geocoder/DSNYCollection',
  'https://a827-donatenyc.nyc.gov/DSNYGeoCoder/api/DSNYCollection',
] as const

/** DSNY pickup weekday → RBC service weekday (evening before pickup). */
export function pickupDayNumberToServiceDay(pickupDay: number): number {
  return (pickupDay + 6) % 7
}

/** RBC service weekday → DSNY pickup weekday. */
export function serviceDayNumberToPickupDay(serviceDay: number): number {
  return (serviceDay + 1) % 7
}

export function pickupSchedulesToServiceSchedules(schedules: DSNYSchedules): DSNYSchedules {
  return {
    garbage: schedules.garbage.map(pickupDayNumberToServiceDay),
    recycling: schedules.recycling.map(pickupDayNumberToServiceDay),
    organics: schedules.organics.map(pickupDayNumberToServiceDay),
    bulk: schedules.bulk.map(pickupDayNumberToServiceDay),
  }
}

export function pickupSchedulesToPickupDayNames(schedules: DSNYSchedules): string[] {
  const nums = [
    ...schedules.garbage,
    ...schedules.recycling,
    ...schedules.organics,
    ...schedules.bulk,
  ]
  return Array.from(new Set(nums.map((n) => DAY_NUMBER_TO_NAME[n])))
}

export function serviceDayNumbersToPickupDayNames(serviceDays: number[]): string[] {
  return Array.from(
    new Set(serviceDays.map((d) => DAY_NUMBER_TO_NAME[serviceDayNumberToPickupDay(d)]))
  )
}

export function parseDSNYResponse(data: any): DSNYSchedules {
  const out: DSNYSchedules = { garbage: [], recycling: [], organics: [], bulk: [] }
  if (!data) return out
  const schedules = data.schedules || data
  if (typeof schedules === 'object') {
    for (const key of Object.keys(out) as Array<keyof DSNYSchedules>) {
      const value = (schedules as any)[key]
      if (Array.isArray(value)) {
        out[key] = value
          .map((v: any) =>
            typeof v === 'number' ? v : dayToNumber[String(v).trim().toLowerCase()]
          )
          .filter((n: any) => typeof n === 'number' && n >= 0 && n <= 6)
      } else if (typeof value === 'string') {
        out[key] = value
          .split(/[,;\s]+/)
          .map((s) => dayToNumber[s.trim().toLowerCase()])
          .filter((n: any) => typeof n === 'number' && n >= 0 && n <= 6)
      }
    }
  }
  // Fallback to NYC DSNY raw response shape: RegularCollectionSchedule, RecyclingCollectionSchedule, etc.
  if (data.RegularCollectionSchedule && out.garbage.length === 0) {
    const days = String(data.RegularCollectionSchedule)
      .split(/[,;\s]+/)
      .map((s) => dayToNumber[s.trim().toLowerCase()])
      .filter((n: any) => typeof n === 'number' && n >= 0 && n <= 6)
    out.garbage = days
  }
  if (data.RecyclingCollectionSchedule && out.recycling.length === 0) {
    const days = String(data.RecyclingCollectionSchedule)
      .split(/[,;\s]+/)
      .map((s) => dayToNumber[s.trim().toLowerCase()])
      .filter((n: any) => typeof n === 'number' && n >= 0 && n <= 6)
    out.recycling = days
  }
  if (data.OrganicsCollectionSchedule && out.organics.length === 0) {
    const days = String(data.OrganicsCollectionSchedule)
      .split(/[,;\s]+/)
      .map((s) => dayToNumber[s.trim().toLowerCase()])
      .filter((n: any) => typeof n === 'number' && n >= 0 && n <= 6)
    out.organics = days
  }
  if (data.BulkPickupCollectionSchedule && out.bulk.length === 0) {
    const days = String(data.BulkPickupCollectionSchedule)
      .split(/[,;\s]+/)
      .map((s) => dayToNumber[s.trim().toLowerCase()])
      .filter((n: any) => typeof n === 'number' && n >= 0 && n <= 6)
    out.bulk = days
  }
  return out
}

async function fetchSchedule(address: string): Promise<DSNYResult | null> {
  const proxyUrl = '/api/dsny-schedule'
  try {
    const proxyResponse = await fetch(proxyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address }),
    })
    if (proxyResponse.ok) {
      const proxyData = await proxyResponse.json()
      // Always normalize: proxy returns lowercase day names in `schedules`, UI expects 0–6.
      const pickupSchedules = parseDSNYResponse({
        ...(proxyData.raw_data || {}),
        schedules: proxyData.schedules,
      })
      return {
        address,
        zone: proxyData.zone || 'Unknown Zone',
        pickupSchedules,
        schedules: pickupSchedulesToServiceSchedules(pickupSchedules),
        nextPickup: proxyData.next_pickup,
        source: 'proxy',
      }
    }
  } catch {
    // ignore - try direct
  }
  for (const base of DSNY_COLLECTION_API_URLS) {
    try {
      const response = await fetch(
        `${base}?address=${encodeURIComponent(address)}`,
        { method: 'GET', headers: { Accept: 'application/json' } }
      )
      if (!response.ok) continue
      const data = await response.json()
      if (data.RegularCollectionSchedule) {
        const pickupSchedules = parseDSNYResponse(data)
        return {
          address,
          zone: data.Zone || 'Unknown Zone',
          pickupSchedules,
          schedules: pickupSchedulesToServiceSchedules(pickupSchedules),
          source: 'direct',
        }
      }
    } catch {
      // try next endpoint
    }
  }
  return null
}

/**
 * Proposal phase: just return counts (frequency-only).
 */
export async function getDSNYFrequencies(address: string): Promise<DSNYFrequencies | null> {
  if (!address) return null
  const res = await fetchSchedule(address)
  if (!res) return null
  const f: DSNYFrequencies = {
    garbage: res.schedules.garbage.length,
    recycling: res.schedules.recycling.length,
    organics: res.schedules.organics.length,
    bulk: res.schedules.bulk.length,
    total: 0,
  }
  const allDays = new Set([
    ...res.pickupSchedules.garbage,
    ...res.pickupSchedules.recycling,
    ...res.pickupSchedules.organics,
    ...res.pickupSchedules.bulk,
  ])
  f.total = allDays.size
  return f
}

/**
 * Contract phase: return service day-of-week numbers per collection type
 * (evening before DSNY pickup), plus raw pickupSchedules for dsny_pickup_days.
 */
export async function getDSNYPickupDays(address: string): Promise<DSNYResult | null> {
  if (!address) return null
  return await fetchSchedule(address)
}
